/**
 * @poc-to-production
 *
 * RequirementsIntelligencePanel
 *
 * The "what applies to this project, and why" surface — replaces the
 * older ComplianceDashboard. It tells the deduction story up front
 * (ACC project metadata → location → applicable code packs) and then
 * surfaces the breadth of applicable requirements grouped by topic.
 *
 * Three cards:
 *
 *   1. Project Requirement Profile — deduced location + code basis +
 *      project type + phase. Expandable "Why this jurisdiction?" trace
 *      shows the ACC/AEC-DM signals that drove the inference.
 *
 *   2. Requirements Matched — donut + counts. Reuses the existing
 *      compliance computation (confirmed / indeterminate / compliant)
 *      so the number lines up with what the engine actually finds.
 *
 *   3. Applicable Requirements — rules grouped by topic with severity
 *      chips. Categories tied to vera ontology classes so we never lie
 *      about what's in the pack; categories we haven't packaged yet
 *      are rendered as "roadmap" rows.
 *
 * Honest framing: the project metadata shown here is a **demo
 * fixture** (apps/web/src/data/projectProfile.ts), not data pulled from
 * a live ACC project. Phase 2 will replace the fixture with an
 * `aecdm-mcp` call to `getProjects` + jurisdiction inference; the
 * panel shape doesn't change.
 */

import type { EvaluatedElement } from "@vera/rules";
import type { Violation } from "@vera/shared";
import { useMemo, useState } from "preact/hooks";
import { isAuthDisabled } from "../auth/apsConfig";
import { useEngineStore } from "../stores/engineStore";
import { useCurrentProject } from "../stores/projectStore";
import { filterViolationsForDisplay } from "../stores/violationFilters";
import { useViolationsStore } from "../stores/violationsStore";
import { Card } from "./Card";

/** Which jurisdiction chip is currently driving the engine (exclusive mode). */
function activeJurisdictionId(
  enabled: Record<string, boolean>
): string | undefined {
  return Object.entries(enabled).find(([, on]) => on)?.[0];
}

/**
 * Category buckets for the "Applicable Requirements" card.
 *
 * Scope policy: each entry is a code area we can in principle evaluate
 * from the BIM model — element geometry, type properties, room/area
 * tags, space program. Anything that lives only in non-BIM artifacts
 * (energy calcs, structural design narratives, MEP discipline files,
 * finish schedules, operational filings) is **excluded by design**
 * and called out in the out-of-scope footnote at the bottom of the
 * card. The roadmap should never imply we'll do energy modeling from
 * a floor plan.
 *
 * Each entry knows the ontology classes it covers so we can count
 * rules accurately. Categories without `appliesTo` are roadmap — the
 * BIM evidence is identified, the rule pack hasn't shipped them yet,
 * so we render a "Roadmap" badge instead of a count.
 *
 * Order: packaged categories first (real rule counts up top), then
 * roadmap categories grouped by code book / chapter.
 */
interface RequirementCategory {
  id: string;
  label: string;
  severity: "high" | "medium" | "low";
  /** Ontology classes whose rule counts roll up into this category. */
  appliesTo?: string[];
  /** One-line note describing the BIM evidence this category reads. */
  description?: string;
  /** Short code-book reference shown next to the label (e.g. "NYCBC Ch.11"). */
  reference?: string;
}

const CATEGORIES_NYC: RequirementCategory[] = [
  // ─── Packaged: rule pack ships these today ──────────────────────
  {
    id: "egress-doors",
    label: "Egress doors & corridors",
    severity: "high",
    reference: "NYCBC Ch.10",
    appliesTo: ["vera:Door", "vera:Opening", "vera:MeansOfEgress"],
    description:
      "Door clear widths, opening forces, emergency-escape openings, egress ceiling heights.",
  },
  {
    id: "stairs",
    label: "Stairs, ramps & headroom",
    severity: "high",
    reference: "NYCBC Ch.10",
    appliesTo: ["vera:Stair", "vera:ProtrudingObject"],
    description:
      "Stair width, tread/riser geometry, headroom clearance, protruding objects in egress paths.",
  },
  {
    id: "fall-protection",
    label: "Guards & handrails",
    severity: "medium",
    reference: "NYCBC Ch.10",
    appliesTo: ["vera:Guard", "vera:Handrail"],
    description:
      "Guard heights at fall-protection edges, handrail height and graspability.",
  },

  // ─── Roadmap: BIM-checkable, not yet packaged ───────────────────
  {
    id: "accessibility",
    label: "Accessibility clearance",
    severity: "high",
    reference: "NYCBC Ch.11 + ICC A117.1",
    description:
      "Door maneuvering clearances, turning circles, accessible route, toilet-room layouts.",
  },
  {
    id: "use-occupancy",
    label: "Use & occupancy classification",
    severity: "high",
    reference: "NYCBC Ch.3",
    description:
      "Occupancy group from room programming (R-2, M, A-2…) — gates downstream rule selection.",
  },
  {
    id: "fire-compartmentation",
    label: "Fire compartmentation & mixed-use separation",
    severity: "high",
    reference: "NYCBC Ch.7 + Ch.508",
    description:
      "Fire-rated walls between R-2 and M, shaft enclosures, opening protectives — from wall type ratings.",
  },
  {
    id: "heights-areas",
    label: "Building heights & areas",
    severity: "high",
    reference: "NYCBC Ch.5",
    description:
      "Allowable area per occupancy + construction type, summed from room/level areas.",
  },
  {
    id: "plumbing-fixtures",
    label: "Plumbing fixture counts",
    severity: "medium",
    reference: "NYC PC Ch.4",
    description:
      "Minimum WC/lav/drinking-fountain counts per occupancy load — from fixture families + room occupancy.",
  },
  {
    id: "light-vent",
    label: "Light & natural ventilation",
    severity: "medium",
    reference: "NYCBC Ch.12",
    description:
      "Room area vs. operable-window area ratios for habitable spaces.",
  },
  {
    id: "zoning-envelope",
    label: "Zoning envelope",
    severity: "high",
    reference: "NYC Zoning Resolution",
    description:
      "Lot coverage, FAR, height/setback, sky-exposure plane — from site geometry + ZR district lookup.",
  },
  {
    id: "exterior-walls",
    label: "Exterior wall openings",
    severity: "medium",
    reference: "NYCBC Ch.14 + Ch.7",
    description:
      "Allowable window/opening area vs. fire separation distance to lot lines.",
  },
  {
    id: "sustainable-roof",
    label: "Sustainable roof zones",
    severity: "low",
    reference: "NYC LL92 / LL94",
    description:
      "Solar-ready / green-roof zoning of roof area — from roof plan geometry.",
  },
];

const CATEGORIES_LA: RequirementCategory[] = [
  {
    id: "egress-doors",
    label: "Egress doors & openings",
    severity: "high",
    reference: "CRC §R311 / R310",
    appliesTo: ["vera:Door", "vera:Opening"],
    description:
      "Egress door clear width/height, emergency escape & rescue openings (EERO).",
  },
  {
    id: "stairs",
    label: "Stairs & landings",
    severity: "high",
    reference: "CRC §R311.7",
    appliesTo: ["vera:Stair"],
    description:
      "Stair width, riser/tread, headroom, landings, walking-surface slope.",
  },
  {
    id: "fall-protection",
    label: "Guards & handrails",
    severity: "medium",
    reference: "CRC §R312",
    appliesTo: ["vera:Guard", "vera:Handrail"],
    description: "Guard and handrail heights along required egress paths.",
  },
  {
    id: "habitable-rooms",
    label: "Habitable rooms",
    severity: "medium",
    reference: "CRC §R304–R305",
    appliesTo: ["vera:Room"],
    description:
      "Minimum room area, ceiling height, light & ventilation ratios.",
  },
];

const CATEGORIES_BCA: RequirementCategory[] = [
  {
    id: "bca-washroom",
    label: "Accessible washroom clear floor",
    severity: "high",
    reference: "BCA CABE §5.1.3",
    appliesTo: ["vera:AccessibleWaterCloset"],
    description:
      "Ray-marched clear floor for larger accessible individual washrooms (1800 × 2100 mm).",
  },
];

const CATEGORIES_BY_JURISDICTION: Record<string, RequirementCategory[]> = {
  "us-ny-nyc": CATEGORIES_NYC,
  "us-ca-la-county": CATEGORIES_LA,
  "sg-bca": CATEGORIES_BCA,
};

function categoriesForJurisdiction(
  jurisdictionId: string | undefined
): RequirementCategory[] {
  if (jurisdictionId && CATEGORIES_BY_JURISDICTION[jurisdictionId]) {
    return CATEGORIES_BY_JURISDICTION[jurisdictionId];
  }
  return CATEGORIES_NYC;
}

/**
 * Code areas we explicitly DON'T promise to evaluate, even on the
 * roadmap. Rendered as a one-line footnote so the pitch surface
 * never implies "Vera does energy modeling from a floor plan."
 */
const OUT_OF_SCOPE_FOR_BIM =
  "Out of scope (not BIM-derivable): energy modeling / LL97 caps, structural design calcs, " +
  "MEP discipline files (mechanical / electrical / plumbing risers), interior-finish flame-spread " +
  "specs, façade-inspection programs (LL11/126), and operational filings (FDNY EAP, DOT BPP).";

const SEVERITY_PILL: Record<
  RequirementCategory["severity"],
  { label: string; bg: string; fg: string }
> = {
  high: {
    label: "High",
    bg: "rgba(214,83,83,0.18)",
    fg: "var(--fc2-violation)",
  },
  medium: { label: "Medium", bg: "rgba(229,184,95,0.18)", fg: "#e5b85f" },
  low: { label: "Low", bg: "rgba(127,179,213,0.18)", fg: "#7fb3d5" },
};

interface MatchedStats {
  totalEvaluable: number;
  confirmed: number;
  indeterminate: number;
  compliant: number;
}

function computeMatchedStats(
  elements: EvaluatedElement[],
  violations: Violation[]
): MatchedStats {
  type Status = "confirmed" | "indeterminate" | "compliant";
  const verdict = new Map<number, Status>();
  for (const el of elements) {
    if (!verdict.has(el.dbId)) verdict.set(el.dbId, "compliant");
  }
  for (const v of violations) {
    if (!v.provenance || v.provenance.source !== "engine") continue;
    const dbId = v.elementDbId;
    if (typeof dbId !== "number") continue;
    const cur = verdict.get(dbId);
    if (v.provenance.status === "confirmed_violation") {
      verdict.set(dbId, "confirmed");
    } else if (v.provenance.status === "indeterminate" && cur !== "confirmed") {
      verdict.set(dbId, "indeterminate");
    }
  }
  let confirmed = 0;
  let indeterminate = 0;
  let compliant = 0;
  for (const s of verdict.values()) {
    if (s === "confirmed") confirmed += 1;
    else if (s === "indeterminate") indeterminate += 1;
    else compliant += 1;
  }
  return {
    totalEvaluable: verdict.size,
    confirmed,
    indeterminate,
    compliant,
  };
}

export function RequirementsIntelligencePanel() {
  const elements = useEngineStore(s => s.elements);
  const rules = useEngineStore(s => s.rules);
  const jurisdictions = useEngineStore(s => s.jurisdictions);
  const enabledJurisdictions = useEngineStore(s => s.enabledJurisdictions);
  const inferredJurisdictionIds = useEngineStore(
    s => s.inferredJurisdictionIds
  );
  const violations = useViolationsStore(s => s.violations);

  const activeJid = activeJurisdictionId(enabledJurisdictions);
  const activeJurisdiction = jurisdictions.find(j => j.id === activeJid);
  const isWhatIf =
    activeJid != null &&
    inferredJurisdictionIds.length > 0 &&
    !inferredJurisdictionIds.includes(activeJid);

  const displayViolations = useMemo(
    () => filterViolationsForDisplay(violations, { enabledJurisdictions }),
    [violations, enabledJurisdictions]
  );

  const stats = useMemo(
    () => computeMatchedStats(elements, displayViolations),
    [elements, displayViolations]
  );

  // Roll up rule counts per category. Only count rules whose
  // jurisdiction is currently enabled — otherwise the donut and the
  // category counts drift apart when the user toggles jurisdictions.
  const activeRules = rules.filter(
    r =>
      enabledJurisdictions[r.source.jurisdictionId] &&
      r.confidence?.tier !== "rejected"
  );
  const totalActive = activeRules.length;
  const countsByClass = new Map<string, number>();
  for (const r of activeRules) {
    const k = r.appliesTo.elementClass;
    countsByClass.set(k, (countsByClass.get(k) ?? 0) + 1);
  }

  // No outer wrapper / scroll: the parent column in App.tsx scrolls
  // the whole stack as one. Each card manages its own collapse.
  return (
    <>
      {isAuthDisabled() ? (
        <ProjectProfileCard
          isWhatIf={isWhatIf}
          evaluatingAs={activeJurisdiction?.displayName}
        />
      ) : null}

      <RequirementsMatchedCard
        totalRules={totalActive}
        elementsEvaluable={stats.totalEvaluable}
        confirmed={stats.confirmed}
        indeterminate={stats.indeterminate}
        compliant={stats.compliant}
        isWhatIf={isWhatIf}
        evaluatingAs={activeJurisdiction?.displayName}
      />

      <ApplicableRequirementsCard
        countsByClass={countsByClass}
        jurisdictionId={activeJid}
        isWhatIf={isWhatIf}
      />

      <SourcesCard
        jurisdictions={jurisdictions.filter(j => enabledJurisdictions[j.id])}
        isWhatIf={isWhatIf}
        evaluatingAs={activeJurisdiction?.displayName}
      />
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* Cards                                                                */
/* ─────────────────────────────────────────────────────────────────── */

function ProjectProfileCard({
  isWhatIf,
  evaluatingAs,
}: {
  isWhatIf: boolean;
  evaluatingAs?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const p = useCurrentProject();

  return (
    <Card
      title="Project Requirement Profile"
      accent="#7fb3d5"
      defaultOpen
      meta={`${p.location.city}, ${p.location.stateCode}`}
    >
      {isWhatIf && evaluatingAs && (
        <div
          style={{
            marginBottom: 10,
            padding: "8px 10px",
            borderRadius: 6,
            background: "rgba(229,184,95,0.12)",
            border: "1px dashed rgba(229,184,95,0.45)",
            fontSize: 11,
            lineHeight: 1.45,
            color: "#e5b85f",
          }}
        >
          <strong>What-if evaluation:</strong> running{" "}
          <span style={{ color: "var(--fc2-text)" }}>{evaluatingAs}</span> rules
          against this model. Project location and deduction below stay fixed —
          this is not a jurisdiction reclassification.
        </div>
      )}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div
          aria-hidden
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            background: "rgba(127,179,213,0.18)",
            border: "1px solid rgba(127,179,213,0.4)",
            color: "#7fb3d5",
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          title="Location pin"
        >
          ◉
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "var(--fc2-text)",
              lineHeight: 1.1,
            }}
          >
            {p.location.city}, {p.location.stateCode}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--fc2-text-dim)",
              marginTop: 2,
            }}
          >
            {p.codeBasis}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 10, display: "grid", gap: 4, fontSize: 12 }}>
        <Row label="Project type" value={p.projectType} />
        <Row label="Phase" value={p.phase} />
        <Row label="Source" value={p.metadataSource} />
      </div>

      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        style={{
          marginTop: 8,
          background: "transparent",
          border: "none",
          padding: 0,
          color: "#7fb3d5",
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
          letterSpacing: 0.3,
        }}
      >
        {expanded ? "Hide deduction trace ▴" : "Why this jurisdiction? ▾"}
      </button>

      {expanded && (
        <div
          style={{
            marginTop: 8,
            padding: 10,
            background: "rgba(127,179,213,0.06)",
            border: "1px solid rgba(127,179,213,0.25)",
            borderRadius: 6,
            fontSize: 11,
            lineHeight: 1.5,
            color: "var(--fc2-text-dim)",
          }}
        >
          <div style={{ marginBottom: 4, color: "var(--fc2-text)" }}>
            Signals from ACC project + AEC DM:
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {p.deductionTrace.map(t => (
              <li key={t.label}>
                <span style={{ color: "var(--fc2-text)" }}>{t.label}:</span>{" "}
                {t.value}
              </li>
            ))}
          </ul>
          <div
            style={{
              marginTop: 6,
              paddingTop: 6,
              borderTop: "1px dashed rgba(127,179,213,0.25)",
              color: "var(--fc2-text)",
            }}
          >
            → Deduced jurisdiction (project metadata):{" "}
            <strong style={{ color: "#7fb3d5" }}>
              {p.inferredJurisdictionIds
                .map(id => id.replace(/^us-/, "").replace(/-/g, " "))
                .join(", ") || `${p.location.city}, ${p.location.stateCode}`}
            </strong>{" "}
            ({p.codeBasis})
          </div>
        </div>
      )}
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", gap: 12 }}>
      <span
        style={{
          flex: "0 0 90px",
          color: "var(--fc2-text-dim)",
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <span style={{ color: "var(--fc2-text)", flex: 1 }}>{value}</span>
    </div>
  );
}

function RequirementsMatchedCard({
  totalRules,
  elementsEvaluable,
  confirmed,
  indeterminate,
  compliant,
  isWhatIf,
  evaluatingAs,
}: {
  totalRules: number;
  elementsEvaluable: number;
  confirmed: number;
  indeterminate: number;
  compliant: number;
  isWhatIf: boolean;
  evaluatingAs?: string;
}) {
  const total = Math.max(elementsEvaluable, 1);
  const pct = (n: number) => `${Math.round((n / total) * 100)}%`;
  const openFindings = confirmed + indeterminate;
  const meta =
    isWhatIf && evaluatingAs
      ? `${totalRules} rules · what-if ${evaluatingAs}`
      : `${totalRules} rules · ${openFindings} open`;
  return (
    <Card title="Requirements Matched" defaultOpen meta={meta}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Donut
          openFindings={openFindings}
          compliant={compliant}
          centerLabel={String(openFindings)}
          centerCaption="open"
        />
        <div style={{ flex: 1, fontSize: 12, lineHeight: 1.5 }}>
          <div style={{ fontWeight: 600, color: "var(--fc2-text)" }}>
            {totalRules} applicable requirements
          </div>
          <div style={{ color: "var(--fc2-text-dim)" }}>
            {elementsEvaluable} elements evaluated
          </div>
          <div style={{ color: "var(--fc2-text-dim)", marginTop: 4 }}>
            <span style={{ color: "#f0a060" }}>{openFindings}</span> open
            findings ({pct(openFindings)}) — not yet clear for submit
          </div>
          <div
            style={{
              color: "var(--fc2-text-dim)",
              marginTop: 2,
              fontSize: 11,
            }}
          >
            Includes{" "}
            <span style={{ color: "var(--fc2-violation)" }}>{confirmed}</span>{" "}
            confirmed violation{confirmed === 1 ? "" : "s"} and{" "}
            <span style={{ color: "#e5b85f" }}>{indeterminate}</span> needing
            evidence
          </div>
          <div style={{ color: "var(--fc2-text-dim)", marginTop: 4 }}>
            <span style={{ color: "#78c896" }}>{compliant}</span> clearly
            compliant on evaluated facts ({pct(compliant)})
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * Inline SVG donut chart — three arcs sized by element verdict. We
 * deliberately don't pull in a charting library: the bundle stays
 * lean and the donut never needs interactivity beyond a tooltip.
 */
function Donut({
  openFindings,
  compliant,
  centerLabel,
  centerCaption,
}: {
  openFindings: number;
  compliant: number;
  centerLabel: string;
  centerCaption: string;
}) {
  const total = Math.max(openFindings + compliant, 1);
  const r = 28;
  const c = 2 * Math.PI * r;
  const seg = (n: number) => (n / total) * c;
  const segOpen = seg(openFindings);
  const segCompliant = seg(compliant);
  return (
    <svg
      width={72}
      height={72}
      viewBox="0 0 72 72"
      role="img"
      aria-label={`${openFindings} open findings, ${compliant} compliant`}
      style={{ flexShrink: 0 }}
    >
      <title>
        {openFindings} open findings · {compliant} compliant
      </title>
      <circle
        cx={36}
        cy={36}
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.06)"
        strokeWidth={8}
      />
      {/* Compliant — green */}
      <circle
        cx={36}
        cy={36}
        r={r}
        fill="none"
        stroke="#78c896"
        strokeWidth={8}
        strokeDasharray={`${segCompliant} ${c - segCompliant}`}
        strokeDashoffset={c / 4}
        transform="rotate(-90 36 36)"
      />
      {/* Open findings — confirmed + needs evidence */}
      <circle
        cx={36}
        cy={36}
        r={r}
        fill="none"
        stroke="#f0a060"
        strokeWidth={8}
        strokeDasharray={`${segOpen} ${c - segOpen}`}
        strokeDashoffset={c / 4 - segCompliant}
        transform="rotate(-90 36 36)"
      />
      <text
        x={36}
        y={38}
        textAnchor="middle"
        fontSize={14}
        fontWeight={700}
        fill="var(--fc2-text)"
      >
        {centerLabel}
      </text>
      <text
        x={36}
        y={50}
        textAnchor="middle"
        fontSize={8}
        fill="var(--fc2-text-dim)"
        style={{ textTransform: "uppercase", letterSpacing: 0.5 }}
      >
        {centerCaption}
      </text>
    </svg>
  );
}

function ApplicableRequirementsCard({
  countsByClass,
  jurisdictionId,
  isWhatIf,
}: {
  countsByClass: Map<string, number>;
  jurisdictionId?: string;
  isWhatIf: boolean;
}) {
  const categories = categoriesForJurisdiction(jurisdictionId);
  const rows = categories.map(c => {
    const count = (c.appliesTo ?? []).reduce(
      (sum, cls) => sum + (countsByClass.get(cls) ?? 0),
      0
    );
    return { category: c, count, hasRules: !!c.appliesTo };
  });
  const packaged = rows.filter(r => r.hasRules);
  const roadmap = rows.filter(r => !r.hasRules);
  const packagedCount = packaged.reduce((s, r) => s + r.count, 0);

  // Roadmap collapses by default — the headline of this card is what
  // we PACKAGE, not what we plan. Users open the roadmap when they
  // want to see breadth; the chevron + count signals there's more.
  const [showRoadmap, setShowRoadmap] = useState(false);

  return (
    <Card
      title="Applicable Requirements"
      defaultOpen
      meta={
        isWhatIf
          ? `${packaged.length + roadmap.length} categories · what-if`
          : `${packaged.length + roadmap.length} categories`
      }
    >
      {isWhatIf && (
        <p
          style={{
            margin: "0 0 10px",
            fontSize: 11,
            lineHeight: 1.45,
            color: "var(--fc2-text-dim)",
          }}
        >
          Categories and counts reflect the code pack you selected under{" "}
          <strong style={{ color: "var(--fc2-text)" }}>Evaluate under</strong>,
          not the project&apos;s deduced location.
        </p>
      )}
      <Subhead
        label="Packaged in this build"
        count={`${packaged.length} categories · ${packagedCount} rules`}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {packaged.map(({ category, count }) => (
          <CategoryRow
            key={category.id}
            category={category}
            countLabel={`${count} rule${count === 1 ? "" : "s"}`}
            dim={false}
          />
        ))}
      </div>

      <div style={{ height: 10 }} />
      <button
        type="button"
        onClick={() => setShowRoadmap(v => !v)}
        aria-expanded={showRoadmap}
        style={{
          width: "100%",
          background: "transparent",
          border: "none",
          padding: 0,
          margin: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          fontSize: 9,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: "var(--fc2-text-dim)",
          marginBottom: showRoadmap ? 6 : 0,
          fontFamily: "inherit",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 8,
              transition: "transform 120ms ease",
              transform: showRoadmap ? "rotate(90deg)" : "rotate(0deg)",
              fontSize: 8,
            }}
          >
            ▶
          </span>
          Roadmap (BIM-checkable, not yet packaged)
        </span>
        <span style={{ fontWeight: 600 }}>{roadmap.length} categories</span>
      </button>
      {showRoadmap && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {roadmap.map(({ category }) => (
            <CategoryRow
              key={category.id}
              category={category}
              countLabel="Roadmap"
              dim
            />
          ))}
        </div>
      )}

      <p
        style={{
          marginTop: 12,
          marginBottom: 0,
          fontSize: 10,
          lineHeight: 1.5,
          color: "var(--fc2-text-dim)",
          borderTop: "1px dashed var(--fc2-border)",
          paddingTop: 8,
        }}
      >
        {OUT_OF_SCOPE_FOR_BIM}
      </p>
    </Card>
  );
}

function Subhead({ label, count }: { label: string; count: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        fontSize: 9,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        color: "var(--fc2-text-dim)",
        marginBottom: 6,
      }}
    >
      <span>{label}</span>
      <span style={{ fontWeight: 600 }}>{count}</span>
    </div>
  );
}

function CategoryRow({
  category,
  countLabel,
  dim,
}: {
  category: RequirementCategory;
  countLabel: string;
  dim: boolean;
}) {
  const pill = SEVERITY_PILL[category.severity];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 8px",
        background: dim ? "transparent" : "rgba(255,255,255,0.025)",
        borderRadius: 4,
        opacity: dim ? 0.62 : 1,
      }}
      title={category.description}
    >
      <span
        aria-hidden
        style={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          border: `1.5px solid ${pill.fg}`,
          color: pill.fg,
          fontSize: 10,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        !
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--fc2-text)",
            display: "flex",
            gap: 6,
            alignItems: "baseline",
            flexWrap: "wrap",
          }}
        >
          <span>{category.label}</span>
          {category.reference && (
            <span
              style={{
                fontSize: 9,
                color: "var(--fc2-text-dim)",
                fontWeight: 400,
                letterSpacing: 0.2,
              }}
            >
              {category.reference}
            </span>
          )}
        </div>
        <div style={{ fontSize: 10, color: "var(--fc2-text-dim)" }}>
          {countLabel}
          {category.description ? ` · ${category.description}` : ""}
        </div>
      </div>
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          padding: "2px 8px",
          borderRadius: 10,
          background: pill.bg,
          color: pill.fg,
          letterSpacing: 0.3,
        }}
      >
        {pill.label}
      </span>
    </div>
  );
}

function SourcesCard({
  jurisdictions,
  isWhatIf,
  evaluatingAs,
}: {
  jurisdictions: { id: string; displayName: string; rulesCount: number }[];
  isWhatIf: boolean;
  evaluatingAs?: string;
}) {
  const p = useCurrentProject();
  const sourceCount = jurisdictions.length + 2;
  return (
    <Card
      title="Requirement Sources"
      defaultOpen={false}
      meta={`${sourceCount} sources${isWhatIf ? " · what-if" : ""}`}
    >
      {isWhatIf && evaluatingAs && (
        <p
          style={{
            margin: "0 0 8px",
            fontSize: 11,
            color: "#e5b85f",
            lineHeight: 1.45,
          }}
        >
          Active code pack: {evaluatingAs} (what-if overlay)
        </p>
      )}
      <ul
        style={{
          margin: 0,
          paddingLeft: 16,
          fontSize: 12,
          lineHeight: 1.7,
          color: "var(--fc2-text)",
        }}
      >
        {jurisdictions.map(j => (
          <li key={j.id}>
            {j.displayName}{" "}
            <span style={{ color: "var(--fc2-text-dim)" }}>
              · {j.rulesCount} rule{j.rulesCount === 1 ? "" : "s"}
            </span>
          </li>
        ))}
        <li>{p.modelSource}</li>
        <li>ACC project metadata + AEC DM hub region</li>
      </ul>
    </Card>
  );
}
